import { ElementType, useState } from "react";
import { Button, Chip, Container, GridLegacy as Grid, Paper, Typography, alpha } from "@mui/material";
import { ArrowBack, ArrowForward, Article, CheckCircle, UploadFile } from "@mui/icons-material";
// AI-Assisted (coming soon): re-add `AutoAwesome` here when re-enabling the card below.
import { teal } from "@mui/material/colors";
import clsx from "clsx";
import { makeStyles, useTheme } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import CardBase from "../../../components/cards/CardBase";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";

const useStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
    height: "100%",
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    border: `1.5px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "border-color .15s, box-shadow .15s, background-color .15s",
    "&:hover": {
      borderColor: theme.palette.secondary.light,
      boxShadow: theme.shadows[2],
    },
  },
  cardSelected: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
    boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
  },
  cardDisabled: {
    cursor: "default",
    opacity: 0.55,
    "&:hover": { borderColor: theme.palette.divider, boxShadow: "none" },
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  desc: {
    color: theme.palette.text.secondary,
  },
  marker: {
    position: "absolute",
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
}));

type Option = {
  key: string;
  label: string;
  description: string;
  Icon: ElementType;
  color: string;
  to?: string;
  disabled?: boolean;
};

export default function AddRiskAssessmentChooser() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [selected, setSelected] = useState<string | null>(null);

  const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;

  const options: Option[] = [
    // AI-Assisted — coming soon. Uncomment (and re-add the AutoAwesome import) to re-enable.
    // {
    //   key: "ai",
    //   label: "AI-Assisted",
    //   description:
    //     "Answer a few questions and AI builds your full assessment for you — pre-filled and ready to review.",
    //   Icon: AutoAwesome,
    //   color: theme.palette.secondary.main,
    //   disabled: true,
    // },
    {
      key: "template",
      label: "Use a Template",
      description:
        "Browse your library of pre-built risk assessment templates and fill in additional details.",
      Icon: Article,
      color: teal[600],
      to: `${raBase}/add/template`,
    },
    {
      key: "upload",
      label: "Upload External",
      description:
        "Upload an existing Word or PDF document into the centralised register.",
      Icon: UploadFile,
      color: theme.palette.secondary.main,
      to: `${raBase}/add/upload`,
    },
  ];

  const selectedOption = options.find((o) => o.key === selected);

  const onContinue = () => {
    if (selectedOption?.to) history.push(selectedOption.to);
  };

  return (
    <Container>
      <CardBase title="Create Risk Assessment" description="Choose how you'd like to get started.">
        <Grid container spacing={3}>
          {options.map((option) => {
            const { Icon } = option;
            const isSelected = selected === option.key;
            return (
              <Grid item xs={12} sm={6} md={4} key={option.key}>
                <Paper
                  variant="outlined"
                  className={clsx(classes.card, {
                    [classes.cardSelected]: isSelected,
                    [classes.cardDisabled]: option.disabled,
                  })}
                  onClick={() => !option.disabled && setSelected(option.key)}
                >
                  {option.disabled && <Chip size="small" label="Coming soon" className={classes.marker} />}
                  {isSelected && !option.disabled && <CheckCircle className={classes.marker} color="secondary" />}
                  <div className={classes.iconWrap} style={{ backgroundColor: alpha(option.color, 0.12), color: option.color }}>
                    <Icon />
                  </div>
                  <Typography variant="h6" className={classes.title}>
                    {option.label}
                  </Typography>
                  <Typography variant="body2" className={classes.desc}>
                    {option.description}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <div className={classes.footer}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() => history.push(raBase)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForward />}
            disabled={!selectedOption?.to}
            onClick={onContinue}
          >
            Continue
          </Button>
        </div>
      </CardBase>
    </Container>
  );
}
